import React, { useCallback, useEffect, useState, useRef } from 'react';
import { AlertBar, Button, Icon, LoadingCircle, Typography, Label } from '@packages/ui/shared';
import {
    ChangeConfidenceClassification,
    ChangeConfidenceResponse,
    CidCvnOcrResponse,
} from '@packages/models/api/cid-cvn';
import { cidCvnActions, useCidCvn, useCidCvnDispatch, useCidCvnState } from '@packages/contexts/cid-cvn';
import { analyticsService, cidCvnService, qmrsService } from '@web/services/singletons';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { useLocation } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { debounce } from 'lodash';
import { Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics';
import { ImagePreviewInfo } from './image-preview-info';
import { LoadingDots } from '@packages/ui/shared/loading-dots';
import { Retailer } from '@packages/models/api';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { useAuthDispatch, fetchAuthUserRetailers, useAuthState } from '@packages/contexts/auth';
import { useDropzone } from 'react-dropzone';
import { useGoogleAnalytics } from '@web/core/google-analytics/use-google-analytics';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';
import RetailerSearchInput from '@web/components/retailer-search-input';

const logger = getLogger('cid-cvn');

const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/heic'];
const ACCEPTED_EXTENSIONS = ['.png', '.jpeg', '.jpg', '.heic'];
const MAX_RETRY = 3;
const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const EXTENSION_TO_MIME: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    heic: 'image/heic',
};

function normalizeFileForUpload(file: File): File {
    if (file.type && ACCEPTED_FILE_TYPES.includes(file.type)) {
        return file;
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    const inferredType = extension ? EXTENSION_TO_MIME[extension] : undefined;
    if (inferredType) {
        return new File([file], file.name, { type: inferredType });
    }
    return file;
}

interface DropzoneRejectionError {
    code: string;
    message: string;
}

interface DropzoneFileRejection {
    file: File;
    errors?: DropzoneRejectionError[];
}

const useStyles = createUseStyles({
    radioContainer: {
        width: '180px',
    },
    dropZone: {
        alignItems: 'center',
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 4,
        display: 'flex',
        justifyContent: 'center',
        height: 144,
        cursor: 'pointer',
    },
    borderBottomGray: {
        borderBottom: `1px solid ${colors.grayThree}`,
    },
    modalBody: {
        position: 'relative',
        minHeight: 240,
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: 'rgba(255,255,255,0.6)',
        backdropFilter: 'blur(1px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        pointerEvents: 'auto',
    },
    tooltip: {
        marginTop: 5,
        maxWidth: 150,
        '& .tooltip-inner': {
            backgroundColor: colors.graySeven,
            borderRadius: 4,
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '20px',
            padding: '3px 8px',
            textAlign: 'center',
            width: 'max-content',
            maxWidth: 185,
            whiteSpace: 'normal',
            display: 'inline-block',
        },
    },
    inputWrapper: {
        position: 'relative',
    },
    retryCount: {
        fontSize: 11,
        color: colors.textDarkSecondary,
        minWidth: 14,
        textAlign: 'center',
    },
    monoInput: {
        fontFamily: '"JetBrains Mono", "Fira Code", "Source Code Pro", "Consolas", monospace',
        letterSpacing: '0.05em',
    },
});

export interface FileState {
    file: File | null;
    isLoading: boolean;
    isSuccessful: boolean;
    previewUrl: string;
    uploadedAt: string | null;
    uploadedBy: string;
}

const initialState: FileState = {
    file: null,
    isLoading: false,
    isSuccessful: false,
    previewUrl: '',
    uploadedAt: null,
    uploadedBy: '',
};

interface ScanImageModalProps {
    show: boolean;
    onHide: () => void;
    uploadedBy: string;
    sessionVin: string;
    modalStep: 'selectRetailer' | 'upload' | 'confirm' | '';
    modelYear: number | undefined;
    setModalStep: React.Dispatch<React.SetStateAction<'selectRetailer' | 'upload' | 'confirm' | ''>>;
}

interface ConfirmState {
    step: 0 | 1;
    cin: string;
    cvn: string;
    original: { cin: string; cvn: string };
    retryCounts: { cin: number; cvn: number };
    retrying: { cin: boolean; cvn: boolean };
    confidence?: {
        cin?: {
            confidence: number;
            classification: ChangeConfidenceClassification;
            reasons?: string[];
        };
        cvn?: {
            confidence: number;
            classification: ChangeConfidenceClassification;
            reasons?: string[];
        };
    };
    shouldBlockConfirm: { cin: boolean; cvn: boolean };
    locked: { cin: boolean; cvn: boolean };
}

function isChangeConfidenceResponseData(data: any): data is ChangeConfidenceResponse {
    if (!data || typeof data !== 'object') return false;
    const candidate = data as Record<string, any>;
    const isField = (f: any) => f && typeof f === 'object' && 'confidence' in f && 'classification' in f;
    return isField(candidate.cin) || isField(candidate.cvn);
}

function normalizeCvn(value: string): string {
    return value
        .trim()
        .replace(/^0[xX]/, '')
        .toUpperCase();
}

export function ScanImageModal({
    show,
    onHide,
    uploadedBy,
    sessionVin,
    modalStep,
    setModalStep,
    modelYear,
}: ScanImageModalProps) {
    const cidCvnDispatch = useCidCvnDispatch();
    const authDispatch = useAuthDispatch();
    const { t } = useTranslation();
    const { account, retailers: accountRetailers } = useAuthState();
    const { abortSignalRef } = useAbortController();
    const { fetchCidCvnData, setCidCvnCheckBlock } = useCidCvn(cidCvnService);
    const { cidCvnBlocked } = useCidCvnState();
    const existingCidCvnList = useCidCvnState().cidCvnList;
    const { googleAnalyticsService } = useGoogleAnalytics();
    const { pathname } = useLocation();
    const classes = useStyles();
    const dropZoneRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const [alertBarMessage, setAlertBarMessage] = useState<{ message: string }>({ message: '' });
    const [cpoAnswer, setCpoAnswer] = useState<boolean | null>(null);
    const [fileState, setFileState] = useState<FileState>(initialState);
    const [isScanRunning, setIsScanRunning] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [ocrResult, setOcrResult] = useState<CidCvnOcrResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedRetailer, setSelectedRetailer] = useState<Retailer>();
    const initialConfirmState: ConfirmState = {
        step: 0,
        cin: '',
        cvn: '',
        original: { cin: '', cvn: '' },
        retryCounts: { cin: 0, cvn: 0 },
        retrying: { cin: false, cvn: false },
        confidence: {},
        shouldBlockConfirm: { cin: false, cvn: false },
        locked: { cin: false, cvn: false },
    };
    const [confirmState, setConfirmState] = useState<ConfirmState>(initialConfirmState);

    const isScanButtonDisabled =
        !fileState.file || cpoAnswer === null || fileState.isLoading || !fileState.isSuccessful;

    const uploadText = !fileState.isLoading
        ? t('cidCvnCheck:scanImageModal.uploadPlaceholder')
        : t('cidCvnCheck:scanImageModal.uploading');

    const classificationStyles: Record<ChangeConfidenceClassification, { border: string; background: string }> = {
        [ChangeConfidenceClassification.Exact]: { border: colors.greenOne, background: colors.greenFour },
        [ChangeConfidenceClassification.WithinTolerance]: { border: colors.yellowOne, background: colors.yellowFour },
        [ChangeConfidenceClassification.Reject]: { border: colors.redOne, background: colors.redFour },
    };

    // Assigning a Retailer to the CID/CVN Check is required to restrict info if the technician is from a different retailer.
    // The Retailer selection uses the same logic as QMRs: the searchParam `canCreateQmr: true` is used for all users.
    // If this param is omitted and the user is a Global user, no retailers will be shown.
    useEffect(() => {
        if (!account || !show) {
            return;
        }

        const fetchRetailers = async () => {
            try {
                const response = await fetchAuthUserRetailers({
                    searchParams: { canCreateQmr: true },
                    qmrsService,
                    authDispatch,
                    signal: abortSignalRef.current,
                });
                if (response?.data.retailers.length === 1) {
                    setSelectedRetailer(response.data.retailers[0]);
                }
            } catch (error) {
                logger.error('Failed to fetch the retailers for the CID/CVN check', { error });
            }
        };

        fetchRetailers();
    }, [abortSignalRef, account, authDispatch, show]);

    function validateFileType(file: File): boolean {
        const extension = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
        if (!ACCEPTED_FILE_TYPES.includes(file.type) && !ACCEPTED_EXTENSIONS.includes(extension)) {
            googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnImageSelect, {
                success: false,
                error_type: 'validation_error',
                error_code: 'invalid_file_type',
            });
            setAlertBarMessage({
                message: t('cidCvnCheck:scanImageModal.invalidFormatError'),
            });
            return false;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnImageSelect, {
                success: false,
                error_type: 'validation_error',
                error_code: 'file_too_large',
            });
            setAlertBarMessage({
                message: t(
                    'cidCvnCheck:scanImageModal.fileTooLarge',
                    `The file size exceeds the ${MAX_FILE_SIZE_MB}MB limit. Please select a smaller file.`,
                    { maxSize: MAX_FILE_SIZE_MB }
                ),
            });
            return false;
        }
        return true;
    }

    function handleDropFile(acceptedFile: File[]) {
        const file = acceptedFile[0];
        if (file && validateFileType(file)) {
            googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnImageSelect, {
                success: true,
            });
            setFileState((prev) => ({
                ...prev,
                file,
            }));
            uploadFile(file);
        }
    }

    const uploadFile = useCallback(
        async (file: File) => {
            setAlertBarMessage({ message: '' });
            setFileState((prev) => ({
                ...prev,
                file,
                isLoading: true,
                isSuccessful: false,
                previewUrl: URL.createObjectURL(file),
                uploadedAt: new Date().toISOString(),
                uploadedBy: uploadedBy ?? 'N/A',
            }));
            debounce(
                async () => {
                    setFileState((prev) => ({
                        ...prev,
                        isLoading: false,
                        isSuccessful: true,
                    }));
                },
                100,
                { leading: true, trailing: false }
            )();
        },
        [uploadedBy]
    );

    function handleDropRejected(rejectedFiles: File[]) {
        rejectedFiles.forEach((rejection) => {
            const candidate = rejection as DropzoneFileRejection | (File & { errors?: DropzoneRejectionError[] });
            const file = 'file' in candidate ? candidate.file : candidate;
            const errors = 'errors' in candidate ? candidate.errors : undefined;
            const isTooLarge = Array.isArray(errors) && errors.some(({ code }) => code === 'file-too-large');
            const exceedsSizeLimit = file?.size ? file.size > MAX_FILE_SIZE_BYTES : false;

            if (isTooLarge || exceedsSizeLimit) {
                googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnImageSelect, {
                    success: false,
                    error_type: 'validation_error',
                    error_code: 'file_too_large',
                });
                setAlertBarMessage({
                    message: t(
                        'cidCvnCheck:scanImageModal.fileTooLarge',
                        `The file size exceeds the ${MAX_FILE_SIZE_MB}MB limit. Please select a smaller file.`,
                        { maxSize: MAX_FILE_SIZE_MB }
                    ),
                });
                return;
            }

            if (file) {
                validateFileType(file);
            }
        });
    }

    const handleHideModal = useCallback(() => {
        if (fileState.previewUrl) {
            URL.revokeObjectURL(fileState.previewUrl);
        }
        setFileState(initialState);
        setCpoAnswer(null);
        setAlertBarMessage({ message: '' });
        setOcrResult(null);
        setLoading(false);
        setIsScanRunning(false);
        setSelectedRetailer(undefined);
        // Don't reset confirmState if cidCvnBlocked to maintain the locked state
        if (!cidCvnBlocked) {
            setConfirmState(initialConfirmState);
        }
        onHide();
    }, [onHide, fileState.previewUrl, cidCvnBlocked]);

    const handleValidateCidCvn = useCallback(async () => {
        if (
            !sessionVin ||
            !fileState.file ||
            !confirmState.cvn ||
            !confirmState.cin ||
            cpoAnswer === null ||
            !selectedRetailer
        ) {
            return;
        }

        if (confirmState.step === 1) {
            let cinValid = true;
            let cvnValid = true;

            if (confirmState.cin !== confirmState.original.cin) {
                cinValid = await handleRetry('cin');
            }
            if (confirmState.cvn !== confirmState.original.cvn) {
                cvnValid = await handleRetry('cvn');
            }

            if (!cinValid || !cvnValid) {
                return;
            }
        }

        setIsValidating(true);
        try {
            const formUploadFile = new FormData();
            formUploadFile.append('file', normalizeFileForUpload(fileState.file));
            const vinPathMatch = pathname.match(/^\/vin\/[^/]+\/?(.*)$/);
            const entryPoint = vinPathMatch
                ? vinPathMatch[1] || 'vin_session_dashboard'
                : pathname.replace(/\//g, '_').replace(/^_/, '');
            analyticsService.logEvent(ANALYTICS_EVENTS.CID_CVN_CHECK_SUBMITTED, {
                moduleName: 'cid_cvn',
                entryPoint,
                cidEntered: true,
                cvnEntered: true,
            });
            const response = await cidCvnService.validateRecord(
                sessionVin,
                normalizeCvn(confirmState.cvn),
                confirmState.cin,
                cpoAnswer,
                selectedRetailer.retailerId,
                formUploadFile,
                {
                    originalCid: confirmState.original.cin,
                    originalCvn: confirmState.original.cvn,
                }
            );

            if (response.success) {
                analyticsService.logEvent(ANALYTICS_EVENTS.CID_CVN_RESULT_RECEIVED, {
                    moduleName: 'cid_cvn',
                    resultStatus: 'pass',
                });

                googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnCheckResult, {
                    success: true,
                    result: response.data.result,
                });

                cidCvnDispatch(
                    cidCvnActions.setCidCvnList({
                        cidCvnList: Array.isArray(existingCidCvnList)
                            ? [
                                  ...existingCidCvnList,
                                  ...(Array.isArray(response.data) ? response.data : [response.data]),
                              ]
                            : Array.isArray(response.data)
                              ? response.data
                              : [response.data],
                    })
                );

                await fetchCidCvnData(sessionVin);
                handleHideModal();
            } else {
                const errorData = response.data as any;
                const errorMessage = errorData?.message || t('cidCvnCheck:scanImageModal.uploadError');
                analyticsService.logEvent(ANALYTICS_EVENTS.CID_CVN_RESULT_RECEIVED, {
                    moduleName: 'cid_cvn',
                    errorMessage,
                    resultStatus: 'error',
                });
                setAlertBarMessage({
                    message: errorMessage,
                });
                googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnCheckResult, {
                    success: false,
                    error_type: 'validation_error',
                });
            }
            setIsValidating(false);
        } catch (error) {
            logger.error(`Failed to validate the CID/CVN for VIN [${sessionVin}]`, { error, vin: sessionVin });
            setAlertBarMessage({
                message: t('cidCvnCheck:scanImageModal.uploadError'),
            });
            googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnCheckResult, {
                success: false,
                error_type: 'unknown_error',
            });
            setIsValidating(false);
        }
    }, [
        existingCidCvnList,
        fileState.file,
        sessionVin,
        cpoAnswer,
        confirmState.cin,
        confirmState.cvn,
        cidCvnService,
        handleHideModal,
        googleAnalyticsService,
    ]);

    const handlePasteFileItem = useCallback(
        (item: DataTransferItem) => {
            const file = item.getAsFile();
            if (file && validateFileType(file)) {
                setFileState((prev) => ({ ...prev, file }));
                uploadFile(file);
            }
        },
        [uploadFile, validateFileType]
    );

    const handlePasteStringItem = useCallback(
        (item: DataTransferItem) => {
            item.getAsString((dataUrl) => {
                (async () => {
                    const response = await fetch(dataUrl);
                    const blob = await response.blob();
                    const file = new File([blob], 'pasted-image.' + blob.type.split('/')[1], {
                        type: blob.type,
                    });
                    if (validateFileType(file)) {
                        setFileState((prev) => ({ ...prev, file }));
                        uploadFile(file);
                    }
                })();
            });
        },
        [uploadFile, validateFileType]
    );

    const handleScanImage = useCallback(async () => {
        if (!sessionVin || !fileState.file) {
            return;
        }
        setIsScanRunning(true);
        try {
            const formUploadFile = new FormData();
            formUploadFile.append('file', normalizeFileForUpload(fileState.file));

            const response = await cidCvnService.scanImageFile(formUploadFile, sessionVin, modelYear);

            if (response.success) {
                if (response.data.cin && response.data.cvn && response.data.vin) {
                    if (response.data.vin.top.value == '') {
                        setAlertBarMessage({
                            message: t(
                                "The uploaded image does not contain the required VIN, CID, or CVN information. Please upload a screenshot from SSM3 or SSM4 only, and ensure the VIN, CID, and CVN values are clearly visible in the image. Since the CID/CVN case type won't be available in TechShare, please call Techline to complete the CID/CVN verification."
                            ),
                        });
                        googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnImageScan, {
                            success: false,
                            error_type: 'validation_error',
                            error_code: 'vin_not_found',
                        });
                    } else if (response.data.vin.top.value !== sessionVin) {
                        setAlertBarMessage({
                            message: t('cidCvnCheck:scanImageModal.vinMisMatchError', {
                                extractedVin: response.data.vin.top.value,
                                sessionVin: sessionVin,
                            }),
                        });
                        googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnImageScan, {
                            success: false,
                            error_type: 'validation_error',
                            error_code: 'vin_mismatch',
                        });
                    } else if (response.data.vin.top.value === sessionVin) {
                        setOcrResult(response.data);
                        setLoading(true);
                        setTimeout(() => {
                            setLoading(false);
                            const scannedCin = response.data.cin?.top?.value || '';
                            const scannedCvn = response.data.cvn?.top?.value || '';
                            setConfirmState({
                                step: 0,
                                cin: scannedCin,
                                cvn: scannedCvn,
                                original: { cin: scannedCin, cvn: scannedCvn },
                                retryCounts: { cin: 0, cvn: 0 },
                                retrying: { cin: false, cvn: false },
                                confidence: {},
                                shouldBlockConfirm: { cin: false, cvn: false },
                                locked: { cin: false, cvn: false },
                            });
                            googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnImageScan, { success: true });
                            setModalStep('confirm');
                        }, 2000);
                    }
                } else if (response.data) {
                    googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnImageScan, {
                        success: false,
                        error_type: 'unknown_error',
                    });
                    setAlertBarMessage({
                        message: t('cidCvnCheck:scanImageModal.uploadError'),
                    });
                }
            } else {
                googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnImageScan, {
                    success: false,
                    error_type: 'unknown_error',
                });
                setAlertBarMessage({
                    message: t('cidCvnCheck:scanImageModal.uploadError'),
                });
            }
            setFileState((prev) => ({
                ...prev,
                isSuccessful: !!response.success && sessionVin === response.data?.vin.top.value,
            }));
        } catch (error) {
            logger.error(`Failed to scan the CID/CVN image for VIN [${sessionVin}]`, { error, vin: sessionVin });
            setFileState((prev) => ({
                ...prev,
                isSuccessful: false,
            }));
            setAlertBarMessage({
                message: t('cidCvnCheck:scanImageModal.uploadError'),
            });

            googleAnalyticsService.track(GoogleAnalyticsEvents.CidCvnImageScan, {
                success: false,
                error_type: 'unknown_error',
            });
        } finally {
            setIsScanRunning(false);
        }
    }, [cidCvnService, fileState.file, sessionVin, setModalStep, setLoading, googleAnalyticsService]);

    const handleRetry = useCallback(
        async (field: 'cin' | 'cvn'): Promise<boolean> => {
            setConfirmState((prev) => ({
                ...prev,
                retrying: { ...prev.retrying, [field]: true },
            }));
            let isValid = true;
            try {
                const body =
                    field === 'cin'
                        ? { extracted_cin: confirmState.original.cin, changed_cin: confirmState.cin }
                        : {
                              extracted_cvn: normalizeCvn(confirmState.original.cvn),
                              changed_cvn: normalizeCvn(confirmState.cvn),
                          };
                const confidenceResp = await cidCvnService.getChangeConfidence(body);
                const data: any = confidenceResp?.data;
                if (isChangeConfidenceResponseData(data)) {
                    const entry = data[field];
                    if (entry) {
                        // Check if confidence is >= 60%
                        isValid = entry.confidence >= 0.6;
                        setConfirmState((prev) => ({
                            ...prev,
                            confidence: {
                                ...prev.confidence,
                                [field]: {
                                    confidence: entry.confidence,
                                    classification: entry.classification,
                                    reasons: entry.reasons,
                                },
                            },
                            shouldBlockConfirm: {
                                ...prev.shouldBlockConfirm,
                                [field]: !isValid,
                            },
                        }));
                    }
                }
            } catch (e) {
                logger.error('Failed to check the CID/CVN change confidence', { error: e, field });
                setAlertBarMessage({ message: t('cidCvnCheck:scanImageModal.uploadError') });
                isValid = false;
            } finally {
                setConfirmState((prev) => {
                    const newCount = prev.retryCounts[field] + 1;
                    const shouldResetField = newCount >= MAX_RETRY;

                    if (newCount >= MAX_RETRY) {
                        setCidCvnCheckBlock(sessionVin);
                    }

                    return {
                        ...prev,
                        [field]: shouldResetField ? prev.original[field] : prev[field],
                        retrying: { ...prev.retrying, [field]: false },
                        retryCounts: { ...prev.retryCounts, [field]: newCount },
                        locked: { ...prev.locked, [field]: newCount >= MAX_RETRY },
                    } as ConfirmState;
                });
            }
            return isValid;
        },
        [cidCvnService, confirmState.cin, confirmState.cvn, confirmState.original.cin, confirmState.original.cvn, t]
    );

    function isFieldInvalid(field: 'cin' | 'cvn'): boolean {
        const meta = confirmState.confidence?.[field];
        if (!meta) return false;
        return meta.confidence < 0.6;
    }

    function getFieldErrorStyle(field: 'cin' | 'cvn'): React.CSSProperties | undefined {
        if (isFieldInvalid(field)) {
            return {
                borderColor: colors.redOne,
                backgroundColor: colors.redFour,
            };
        }
        return undefined;
    }

    function renderFieldError(field: 'cin' | 'cvn') {
        const attemptsLeft = MAX_RETRY - confirmState.retryCounts[field];
        const fieldLabel = field === 'cin' ? 'CID' : 'CVN';

        if (confirmState.locked[field] || attemptsLeft === 0) {
            return (
                <div style={{ marginTop: 4 }}>
                    <Typography variant="caption" color="redOne">
                        {t(
                            'cidCvnCheck:scanImageModal.maxAttemptsReached',
                            'Maximum attempts reached. Contact Techshare for support: techshare_support@subaru.com'
                        )}
                    </Typography>
                </div>
            );
        }

        if (!isFieldInvalid(field)) return null;

        return (
            <div style={{ marginTop: 4 }}>
                <Typography variant="caption" color="redOne">
                    {t(
                        'cidCvnCheck:scanImageModal.invalidRangeError',
                        `Entered ${fieldLabel} code is outside the valid range. Attempts left: ${attemptsLeft}`,
                        { field: fieldLabel, attempts: attemptsLeft }
                    )}
                </Typography>
            </div>
        );
    }

    function handleRetailerSelected() {
        setModalStep('upload');
    }

    function handlePrimaryButtonClick() {
        if (modalStep === 'selectRetailer') {
            handleRetailerSelected();
        } else if (modalStep === 'confirm') {
            handleValidateCidCvn();
        } else {
            handleScanImage();
        }
    }

    useEffect(() => {
        if (!show) {
            return;
        }

        function handlePaste(event: ClipboardEvent) {
            if (!modalRef.current) {
                return;
            }
            const items = event.clipboardData?.items;
            if (!items || items.length === 0) {
                return;
            }
            let handled = false;
            for (const item of items) {
                if (item.kind === 'file') {
                    handlePasteFileItem(item);
                    handled = true;
                    break;
                }
                if (item.kind === 'string' && item.type.startsWith('image/')) {
                    handlePasteStringItem(item);
                    handled = true;
                    break;
                }
            }
            if (!handled) {
                setAlertBarMessage({
                    message: t('cidCvnCheck:scanImageModal.invalidFormatError'),
                });
            }
        }

        window.addEventListener('paste', handlePaste);
        return () => {
            window.removeEventListener('paste', handlePaste);
        };
    }, [show, uploadFile, handlePasteFileItem, handlePasteStringItem]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleDropFile,
        onDropRejected: handleDropRejected,
        accept: [...ACCEPTED_FILE_TYPES, ...ACCEPTED_EXTENSIONS].join(','),
        maxSize: MAX_FILE_SIZE_BYTES,
        disabled: fileState.isLoading,
        multiple: false,
    });

    return (
        <Modal show={show} onHide={handleHideModal} centered size="lg" data-testid="scan-image-modal">
            <div ref={modalRef}>
                <Modal.Header>
                    <Modal.Title>
                        {modalStep === 'selectRetailer'
                            ? t('cidCvnCheck:scanImageModal.selectRetailerTitle')
                            : modalStep === 'confirm'
                              ? t('cidCvnCheck:scanImageModal.confirmTitle')
                              : t('cidCvnCheck:scanImageModal.title')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="d-flex flex-column">
                    {loading ? (
                        <div
                            className="d-flex flex-column align-items-center justify-content-center"
                            style={{ minHeight: 240 }}
                        >
                            <LoadingCircle />
                            <div className="mt-2">
                                <Typography color="textDarkSecondary">
                                    Loading
                                    <LoadingDots />
                                </Typography>
                            </div>
                        </div>
                    ) : modalStep === 'selectRetailer' ? (
                        <>
                            <Label style={{ fontWeight: 400 }} required>
                                {t('cidCvnCheck:scanImageModal.selectRetailerLabel')}
                            </Label>
                            <RetailerSearchInput selectedRetailer={selectedRetailer} onChange={setSelectedRetailer} />
                        </>
                    ) : modalStep === 'confirm' && ocrResult ? (
                        <div className="d-flex flex-column gap-4">
                            <AlertBar
                                show={confirmState.step === 1}
                                warning={true}
                                iconHide={true}
                                onClose={() => {}}
                                style={{ marginBottom: 24 }}
                            >
                                <Typography variant="default">
                                    {t('cidCvnCheck:scanImageModal.warningMessage')}
                                    <br />
                                    {t('cidCvnCheck:scanImageModal.warningInfoMessage')}
                                </Typography>
                            </AlertBar>
                            <div>
                                <div className="d-flex align-items-center gap-2">
                                    <Typography variant="caption3">
                                        {t('cidCvnCheck:scanImageModal.confirmationMessage')}
                                    </Typography>
                                    <OverlayTrigger
                                        key={'info'}
                                        placement="top"
                                        overlay={
                                            <Tooltip className={classes.tooltip} id={'info-tool-tip'}>
                                                {t('cidCvnCheck:scanImageModal.toolTipMessage')}
                                            </Tooltip>
                                        }
                                    >
                                        <Icon name="info" color="black" />
                                    </OverlayTrigger>
                                </div>
                                <Form.Group className="d-flex flex-column mt-4">
                                    <div className="row">
                                        <div className="col">
                                            <Form.Label
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Typography variant="default">
                                                    {t('cidCvnCheck:scanImageModal.cidLabel')}
                                                </Typography>
                                                {confirmState.step === 0 && (
                                                    <Typography variant="caption" color="textDarkSecondary">
                                                        {t('cidCvnCheck:scanImageModal.scanned')}
                                                    </Typography>
                                                )}
                                            </Form.Label>
                                            <div className={classes.inputWrapper}>
                                                <div className={classes.inputWrapper}>
                                                    <Form.Control
                                                        type="text"
                                                        value={confirmState.cin}
                                                        className={classes.monoInput}
                                                        disabled={
                                                            confirmState.step === 0 ||
                                                            confirmState.retrying.cin ||
                                                            confirmState.locked.cin
                                                        }
                                                        onChange={(e) =>
                                                            confirmState.step === 1 &&
                                                            setConfirmState((prev) => {
                                                                const next = { ...prev };
                                                                next.cin = e.target.value;
                                                                if (next.confidence?.cin) {
                                                                    const { cin, ...rest } = next.confidence;
                                                                    next.confidence = { ...rest };
                                                                }
                                                                next.shouldBlockConfirm = {
                                                                    ...next.shouldBlockConfirm,
                                                                    cin: false,
                                                                };
                                                                return next;
                                                            })
                                                        }
                                                        readOnly={confirmState.step === 0}
                                                        style={
                                                            confirmState.step === 1
                                                                ? getFieldErrorStyle('cin')
                                                                : undefined
                                                        }
                                                    />
                                                </div>
                                                {confirmState.step === 1 && renderFieldError('cin')}
                                            </div>
                                        </div>
                                        <div className="col">
                                            <Form.Label
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Typography variant="default">
                                                    {t('cidCvnCheck:scanImageModal.cvnLabel')}
                                                </Typography>
                                                {confirmState.step === 0 && (
                                                    <Typography variant="caption" color="textDarkSecondary">
                                                        {t('cidCvnCheck:scanImageModal.scanned')}
                                                    </Typography>
                                                )}
                                            </Form.Label>
                                            <div className={classes.inputWrapper}>
                                                <div className={classes.inputWrapper}>
                                                    <Form.Control
                                                        type="text"
                                                        value={confirmState.cvn}
                                                        className={classes.monoInput}
                                                        disabled={
                                                            confirmState.step === 0 ||
                                                            confirmState.retrying.cvn ||
                                                            confirmState.locked.cvn
                                                        }
                                                        onChange={(e) =>
                                                            confirmState.step === 1 &&
                                                            setConfirmState((prev) => {
                                                                const next = { ...prev };
                                                                next.cvn = e.target.value;
                                                                if (next.confidence?.cvn) {
                                                                    const { cvn, ...rest } = next.confidence;
                                                                    next.confidence = { ...rest };
                                                                }
                                                                next.shouldBlockConfirm = {
                                                                    ...next.shouldBlockConfirm,
                                                                    cvn: false,
                                                                };
                                                                return next;
                                                            })
                                                        }
                                                        readOnly={confirmState.step === 0}
                                                        style={
                                                            confirmState.step === 1
                                                                ? getFieldErrorStyle('cvn')
                                                                : undefined
                                                        }
                                                    />
                                                </div>
                                                {confirmState.step === 1 && renderFieldError('cvn')}
                                            </div>
                                        </div>
                                    </div>
                                </Form.Group>
                            </div>
                            <div className="d-flex justify-content-start gap-2">
                                <Button
                                    variant="ghost-blue"
                                    disabled={
                                        confirmState.step === 1 &&
                                        confirmState.cin === confirmState.original.cin &&
                                        confirmState.cvn === confirmState.original.cvn
                                    }
                                    onPress={() => {
                                        if (confirmState.step === 0) {
                                            setConfirmState((prev) => ({ ...prev, step: 1 }));
                                        } else {
                                            setConfirmState((prev) => ({
                                                ...prev,
                                                cin: prev.original.cin,
                                                cvn: prev.original.cvn,
                                                retrying: { cin: false, cvn: false },
                                                confidence: {},
                                                shouldBlockConfirm: { cin: false, cvn: false },
                                            }));
                                        }
                                    }}
                                >
                                    {confirmState.step === 0
                                        ? t('cidCvnCheck:scanImageModal.confirmButton0Message')
                                        : t('cidCvnCheck:scanImageModal.confirmButton1Message')}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        modalStep === 'upload' && (
                            <>
                                <AlertBar
                                    show={!!alertBarMessage.message}
                                    error={true}
                                    onClose={() => setAlertBarMessage({ message: '' })}
                                    style={{ marginBottom: 24 }}
                                >
                                    {alertBarMessage.message}
                                </AlertBar>
                                <Form.Group className="d-flex flex-column">
                                    <Typography>
                                        {t('cidCvnCheck:scanImageModal.cpoQuestion')}
                                        <Typography color="redOne"> *</Typography>
                                    </Typography>
                                    <div
                                        className={`d-flex flex-row mt-3 pl-1 justify-content-between ${classes.radioContainer}`}
                                    >
                                        <Form.Check
                                            type="radio"
                                            label="Yes"
                                            name="cpo-inspection"
                                            id="cpo-inspection-yes"
                                            checked={cpoAnswer === true}
                                            onChange={() => setCpoAnswer(true)}
                                        />
                                        <Form.Check
                                            type="radio"
                                            label="No"
                                            name="cpo-inspection"
                                            id="cpo-inspection-no"
                                            checked={cpoAnswer === false}
                                            onChange={() => setCpoAnswer(false)}
                                        />
                                    </div>
                                </Form.Group>
                                <div className={`${classes.borderBottomGray} mt-6 mb-6`} />
                                <div {...getRootProps()} ref={dropZoneRef} className={classes.dropZone}>
                                    <input {...getInputProps()} />
                                    <div className="text-center">
                                        <div className="d-flex justify-content-center mb-2">
                                            {fileState.isLoading ? (
                                                <LoadingCircle />
                                            ) : (
                                                <Icon name="export" color="blueOne" />
                                            )}
                                        </div>
                                        <p style={{ fontSize: '15px', marginBottom: '2px' }}>{uploadText}</p>
                                        <small style={{ fontSize: '13px', color: colors.textDarkSecondary }}>
                                            {t('cidCvnCheck:scanImageModal.uploadImageTypes', {
                                                acceptedFileTypes: '.png, .jpg, .jpeg, .heic',
                                            })}
                                        </small>
                                    </div>
                                </div>

                                {fileState.file && !fileState.isLoading && (
                                    <ImagePreviewInfo
                                        fileName={fileState.file.name}
                                        isSuccessful={fileState.isSuccessful}
                                        previewUrl={fileState.previewUrl}
                                        uploadedAt={fileState.uploadedAt}
                                        uploadedBy={fileState.uploadedBy}
                                        inProgress={isScanRunning}
                                    />
                                )}
                            </>
                        )
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex w-100 justify-content-between">
                        {modalStep === 'confirm' &&
                            ocrResult &&
                            (confirmState.step === 1 ? (
                                <OverlayTrigger
                                    key={'back'}
                                    placement="bottom"
                                    overlay={
                                        <Tooltip className={classes.tooltip} id={'back'}>
                                            {t('cidCvnCheck:scanImageModal.backHoverMessage')}
                                        </Tooltip>
                                    }
                                >
                                    <Button
                                        variant="ghost-blue"
                                        onPress={() =>
                                            setConfirmState((prev) => ({
                                                ...prev,
                                                step: 0,
                                                cin: prev.original.cin,
                                                cvn: prev.original.cvn,
                                                retrying: { cin: false, cvn: false },
                                                shouldBlockConfirm: { cin: false, cvn: false },
                                                // Preserve retryCounts, locked, and confidence to maintain retry state
                                            }))
                                        }
                                    >
                                        {t('buttons:back')}
                                    </Button>
                                </OverlayTrigger>
                            ) : (
                                <Button variant="ghost-blue" onPress={() => setModalStep('upload')}>
                                    {t('buttons:back')}
                                </Button>
                            ))}

                        {modalStep === 'upload' && Array.isArray(accountRetailers) && accountRetailers.length > 1 && (
                            <Button variant="ghost-blue" onPress={() => setModalStep('selectRetailer')}>
                                {t('buttons:back')}
                            </Button>
                        )}
                        <div className={`d-flex${modalStep === 'confirm' ? '' : ' ms-auto'}`}>
                            <Button variant="ghost-blue" onPress={() => handleHideModal()} style={{ marginRight: 2 }}>
                                {t('buttons:cancel')}
                            </Button>
                            <OverlayTrigger
                                key={'confirm-disabled'}
                                placement="bottom"
                                show={
                                    modalStep === 'confirm' &&
                                    (cidCvnBlocked ||
                                        (confirmState.step === 1 &&
                                            (confirmState.locked.cin ||
                                                confirmState.locked.cvn ||
                                                (confirmState.cin === confirmState.original.cin &&
                                                    confirmState.cvn === confirmState.original.cvn))))
                                        ? undefined
                                        : false
                                }
                                overlay={
                                    <Tooltip className={classes.tooltip} id={'confirm-disabled-tooltip'}>
                                        {cidCvnBlocked || confirmState.locked.cin || confirmState.locked.cvn
                                            ? t(
                                                  'cidCvnCheck:scanImageModal.contactSupport',
                                                  'Contact Techshare for support: techshare_support@subaru.com'
                                              )
                                            : t(
                                                  'cidCvnCheck:scanImageModal.changeCidCvnToValidate',
                                                  'Please change the CID/CVN to validate'
                                              )}
                                    </Tooltip>
                                }
                            >
                                <span>
                                    <Button
                                        disabled={
                                            modalStep === 'selectRetailer'
                                                ? !selectedRetailer
                                                : isScanButtonDisabled ||
                                                  loading ||
                                                  cidCvnBlocked ||
                                                  confirmState.locked.cin ||
                                                  confirmState.locked.cvn ||
                                                  confirmState.shouldBlockConfirm['cin'] ||
                                                  confirmState.shouldBlockConfirm['cvn'] ||
                                                  (modalStep === 'confirm' &&
                                                      confirmState.step === 1 &&
                                                      confirmState.cin === confirmState.original.cin &&
                                                      confirmState.cvn === confirmState.original.cvn)
                                        }
                                        isLoading={isScanRunning || loading || isValidating}
                                        onPress={handlePrimaryButtonClick}
                                        variant="primary"
                                    >
                                        {modalStep === 'selectRetailer'
                                            ? t('buttons:continue')
                                            : modalStep === 'confirm'
                                              ? t('cidCvnCheck:scanImageModal.confirm')
                                              : t('cidCvnCheck:scanImageModal.scan')}
                                    </Button>
                                </span>
                            </OverlayTrigger>
                        </div>
                    </div>
                </Modal.Footer>
            </div>
        </Modal>
    );
}
